import React, {
  useState,
  useEffect,
  memo,
  useCallback,
  forwardRef,
} from 'react';
import { FlatList, View, StyleSheet, RefreshControl } from 'react-native';
import { Text, ActivityIndicator, useTheme } from 'react-native-paper';
import {
  useInfiniteRecipes,
  useBulkRecipeInteractions,
  useRecipeInteractions,
} from '@coffeeplexity/api-client';
import {
  RecipeListParams,
  Recipe,
  useRecipePrefsItem,
  useRecipePrefsStore,
} from '@coffeeplexity/shared';
import { RecipeCard } from '@coffeeplexity/ui';
import { Coffee } from 'lucide-react-native';

interface RecipeListProps {
  params?: RecipeListParams;
  onRecipePress: (recipe: Recipe) => void;
  onScroll?: (event: any) => void;
}

// Memoized RecipeCard wrapper to prevent unnecessary re-renders
const RecipeCardItem = memo(
  ({
    recipe,
    onRecipePress,
    refetchInteractions,
  }: {
    recipe: Recipe;
    onRecipePress: (recipe: Recipe) => void;
    refetchInteractions: () => void;
  }) => {
    const { isLiked, isBookmarked, toggleLike, toggleBookmark } =
      useRecipePrefsItem(recipe.id);

    const { like, unlike, bookmark, unbookmark } = useRecipeInteractions(
      recipe.id
    );

    const handleLike = useCallback(async () => {
      const newState = toggleLike();
      if (newState) {
        like();
      } else {
        unlike();
      }
      refetchInteractions();
    }, [toggleLike, like, unlike, refetchInteractions]);

    const handleBookmark = useCallback(async () => {
      const newState = toggleBookmark();
      if (newState) {
        bookmark();
      } else {
        unbookmark();
      }
      refetchInteractions();
    }, [toggleBookmark, bookmark, unbookmark, refetchInteractions]);

    return (
      <RecipeCard
        recipe={recipe}
        onPress={onRecipePress}
        onLike={handleLike}
        onBookmark={handleBookmark}
        liked={isLiked}
        bookmarked={isBookmarked}
        variant='default'
        testID={`recipe-${recipe.id}`}
      />
    );
  }
);

RecipeCardItem.displayName = 'RecipeCardItem';

export const RecipeList = forwardRef<FlatList, RecipeListProps>(
  ({ params, onRecipePress, onScroll }, ref) => {
    const theme = useTheme();
    const [refreshing, setRefreshing] = useState(false);
    const {
      data,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      isLoading,
      error,
      refetch,
    } = useInfiniteRecipes(params);

    const recipes =
      data?.pages.flatMap(page => page.items).filter(Boolean) || [];
    const recipeIds = recipes.map(r => r.id);

    // Fetch bulk interaction status from Redis (with DB fallback)
    const { data: bulkInteractions, refetch: refetchInteractions } =
      useBulkRecipeInteractions(recipeIds, recipeIds.length > 0);

    // Sync bulk interactions with local store
    const setLike = useRecipePrefsStore(s => s.setLike);
    const setBookmark = useRecipePrefsStore(s => s.setBookmark);

    useEffect(() => {
      if (bulkInteractions) {
        // Sync Redis/DB data with local Zustand store
        Object.entries(bulkInteractions).forEach(([recipeId, status]) => {
          setLike(recipeId, status.is_liked);
          setBookmark(recipeId, status.is_bookmarked);
        });
      }
    }, [bulkInteractions, setLike, setBookmark]);

    const handleLoadMore = useCallback(() => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const handleRefresh = useCallback(async () => {
      setRefreshing(true);
      try {
        await Promise.all([refetch(), refetchInteractions()]);
      } finally {
        setRefreshing(false);
      }
    }, [refetch, refetchInteractions]);

    const renderRecipe = useCallback(
      ({ item }: { item: Recipe }) => {
        return (
          <RecipeCardItem
            recipe={item}
            onRecipePress={onRecipePress}
            refetchInteractions={refetchInteractions}
          />
        );
      },
      [onRecipePress, refetchInteractions]
    );

    const renderFooter = () => {
      if (!isFetchingNextPage) return null;

      return (
        <View style={styles.footer}>
          <ActivityIndicator size='small' color={theme.colors.primary} />
        </View>
      );
    };

    const renderEmpty = () => {
      if (isLoading) {
        return (
          <View style={styles.centered}>
            <ActivityIndicator size='large' color={theme.colors.primary} />
          </View>
        );
      }

      if (error) {
        return (
          <View style={styles.centered}>
            <Text
              variant='titleMedium'
              style={[styles.emptyTitle, { color: theme.colors.error }]}
            >
              Failed to load recipes
            </Text>
            <Text
              variant='bodyMedium'
              style={[
                styles.emptySubtitle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Please try again
            </Text>
          </View>
        );
      }

      return (
        <View style={styles.centered}>
          <Coffee size={64} color={theme.colors.onSurfaceVariant} />
          <Text
            variant='titleMedium'
            style={[
              styles.emptyTitle,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            No recipes found
          </Text>
          <Text
            variant='bodyMedium'
            style={[
              styles.emptySubtitle,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            Try adjusting your search or filters
          </Text>
        </View>
      );
    };

    return (
      <FlatList
        ref={ref}
        data={recipes}
        renderItem={renderRecipe}
        keyExtractor={item => item?.id}
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={renderFooter}
        ListEmptyComponent={renderEmpty}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[theme.colors.primary]}
          />
        }
        contentContainerStyle={[
          styles.container,
          recipes.length === 0 && styles.emptyContainer,
        ]}
        showsVerticalScrollIndicator={false}
        onScroll={onScroll}
        scrollEventThrottle={16}
        removeClippedSubviews={true}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={50}
        initialNumToRender={10}
        windowSize={10}
      />
    );
  }
);

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  emptyContainer: {
    flex: 1,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  footer: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  emptyTitle: {
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptySubtitle: {
    textAlign: 'center',
    paddingHorizontal: 32,
  },
});
